import React, { useCallback } from 'react';
import { FileText, Download } from 'lucide-react';
import { tokenStorage } from '../utils/tokenStorage';
import { getFileExtension } from '../utils/fileExtension';

interface NonPdfFallbackProps {
  documentUrl: string;
  documentName: string;
  pageNumber?: number;
  className?: string;
}

/**
 * Fallback viewer shown when a non-PDF document (e.g. .doc, .docx) is opened
 * via the citation sidebar. Offers a download button since pdf.js can only
 * render PDFs.
 */
export const NonPdfFallback: React.FC<NonPdfFallbackProps> = ({
  documentUrl,
  documentName,
  pageNumber,
  className = '',
}) => {
  const handleDownload = useCallback(async () => {
    try {
      const token = tokenStorage.getToken();
      const response = await fetch(documentUrl, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
        credentials: 'include',
      });
      if (!response.ok) throw new Error('Download failed');

      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = documentName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      setTimeout(() => window.URL.revokeObjectURL(url), 60_000);
    } catch {
      alert('Failed to download document. Please try again.');
    }
  }, [documentUrl, documentName]);

  const ext = getFileExtension(documentName);

  return (
    <div className={`flex items-center justify-center h-full min-h-[300px] ${className}`}>
      <div className="text-center px-6 py-10 max-w-sm">
        <FileText className="h-16 w-16 mx-auto mb-4 text-medical-400 dark:text-medical-500" />

        <p className="text-sm font-medium text-medical-900 dark:text-medical-100 mb-1 break-all">
          {documentName}
        </p>

        <p className="text-xs text-medical-500 dark:text-medical-400 mb-1">
          .{ext} files cannot be previewed in the browser.
        </p>

        {pageNumber && pageNumber > 0 && (
          <p className="text-xs text-primary-600 dark:text-primary-400 mb-4">
            The cited passage is on page {pageNumber}.
          </p>
        )}

        {(!pageNumber || pageNumber <= 0) && <div className="mb-4" />}

        <button
          onClick={handleDownload}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-primary-600 hover:bg-primary-700 text-white text-sm font-medium transition-colors"
        >
          <Download className="h-4 w-4" />
          Download to view
        </button>
      </div>
    </div>
  );
};
